'use client'

import type { ImgHTMLAttributes } from 'react'
import { useEffect, useState } from 'react'
import slugify from '@sindresorhus/slugify'
import { containerWidthMap, paddingMap } from '@lib/token-maps'
import { useSearchParams } from 'next/navigation'
import { CardImage, CardImageBlock } from '@shc/ui'
import { contentTypeBasePathMap } from '@lib/constants'
import type { ContentfulNewsArticles } from '@lib/types'
import ClientPagination from '@components/client-pagination'
import { type TypeHealthNewsStoriesBlockFields } from '@lib/generated-types'
import { getNewsArticlesAction } from '@lib/services/actions/news-articles-service'
import Analytics from '@components/analytics'
import ImageContentful from '@components/image-contentful'

const HealthNewsStoriesBlock = ({
  sectionLink,
  topics,
  authors,
  articlesToExclude,
  cardsMax,
  width,
  isPaginated,
  paddingTop,
  paddingBottom,
}: TypeHealthNewsStoriesBlockFields) => {
  const searchParams = useSearchParams()
  const currentPageNumber = Number(searchParams?.get('page')) || 1
  const [relatedNews, setRelatedNews] = useState<ContentfulNewsArticles | null>(null)

  useEffect(() => {
    const getNews = async () => {
      const articles = await getNewsArticlesAction(
        topics?.length ? topics.map((topic) => topic.sys.id) : undefined,
        articlesToExclude?.length
          ? articlesToExclude.map((article) => article.fields.slug)
          : undefined,
        authors?.length ? authors.map((author) => author.sys.id) : undefined,
        cardsMax,
        (currentPageNumber - 1) * cardsMax
      )
      setRelatedNews(articles)
    }

    getNews()
  }, [currentPageNumber])

  if (relatedNews?.data.length) {
    return (
      <>
        <CardImageBlock
          id={sectionLink && slugify(sectionLink)}
          paddingBottom={paddingMap[paddingBottom]}
          paddingTop={paddingMap[paddingTop]}
          containerSize={containerWidthMap[width]}
          data-block="healthNewsStoriesBlock">
          {relatedNews?.data.map(({ fields, sys }, idx) => {
            const mediaElement = fields.image // e.g. captionedMedia Contentful Element
            const isTopThree = idx < 3 // set the top 3 to load eagerly
            return (
              <Analytics
                key={sys.id}
                snowplow={{
                  contexts: [
                    {
                      name: 'component',
                      data: {
                        component_text: fields.title,
                        component_url: contentTypeBasePathMap['newsArticle'] + fields.slug,
                      },
                    },
                  ],
                }}>
                <CardImage
                  description={fields.summary}
                  heading={fields.title}
                  href={contentTypeBasePathMap['newsArticle'] + fields.slug}
                  slots={{ image: ImageContentful }}
                  slotProps={{
                    imageContainer: { className: 'relative aspect-video' },
                    image: {
                      image: mediaElement.fields?.image,
                      breakpointWidths: {
                        xs: 380,
                        sm: 287,
                        md: 350,
                        lg: 307,
                        xl: 367,
                        max: 452,
                      },
                      aspectRatioOverride: mediaElement.fields.aspectRatio,
                      focalPoint: mediaElement.fields.focalPoint,
                      isAboveTheFold: isTopThree,
                    } as ImgHTMLAttributes<HTMLImageElement>,
                  }}
                />
              </Analytics>
            )
          })}
        </CardImageBlock>
        {isPaginated && relatedNews?.total > cardsMax && (
          <ClientPagination
            currentPageNumber={currentPageNumber}
            total={relatedNews?.total}
            pageSize={cardsMax}
            scrollTo={slugify(sectionLink ?? '')}
            className="col-span-12 pb-15"
          />
        )}
      </>
    )
  }
}

export default HealthNewsStoriesBlock
